import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useRoles } from '@/features/team/queries'
import { useDirectoryMembers } from '@/features/agents/queries'

type RoleMode = 'all' | 'specific'
type Cfg = { roleMode: RoleMode; pickedUserIds: Set<number> }

export interface RoleMemberShareValue {
  sharedRoleIds: number[]
  sharedUserIds: number[]
}

interface Props {
  /**
   * Current shared selection. The picker re-builds its per-role state
   * from this on first mount and whenever the directory data resolves.
   * Keep this stable (memoize / useState in parent) so the picker
   * doesn't reset on every keystroke in the form.
   */
  value: RoleMemberShareValue
  /**
   * Called whenever the user picks/un-picks a role or member, with the
   * already-flattened sharedRoleIds + sharedUserIds the API expects.
   */
  onChange: (next: RoleMemberShareValue) => void
  /**
   * The viewer's user_id. Their entry is dropped from the per-role
   * member list because the creator of an agent/workflow always has
   * implicit access.
   */
  currentUserId: number | null
  /**
   * Show a one-line summary above the role list. Set to false if the
   * parent already explains what the section is for.
   */
  showHeader?: boolean
}

/**
 * Reusable picker for "share with roles and/or specific members". Used
 * inside the agent and workflow editor modals.
 *
 * UX:
 *   - User checks one or more roles.
 *   - Each checked role expands to a sub-section with two modes:
 *       "All members"     → role goes to sharedRoleIds (every active
 *                            member of that role inherits access).
 *       "Specific members"→ specific user_ids from the role's roster
 *                            go to sharedUserIds; the role itself does
 *                            NOT get the blanket grant.
 *
 * The two output arrays map exactly onto the agent_shares /
 * agent_role_shares (and workflow analogue) tables on the backend.
 */
export function RoleMemberSharePicker({ value, onChange, currentUserId, showHeader = true }: Props) {
  const rolesQ = useRoles()
  const directoryQ = useDirectoryMembers(true)

  const [openRoles, setOpenRoles] = useState<Set<number>>(new Set())
  const [roleSettings, setRoleSettings] = useState<Map<number, Cfg>>(new Map())
  const [roleSearch, setRoleSearch] = useState('')
  const [memberSearch, setMemberSearch] = useState<Record<number, string>>({})

  // Hydrate state from `value` once roles + directory are available so
  // we can group sharedUserIds by their role on first mount and
  // whenever the parent passes a fresh value (e.g. after the row's GET
  // resolves).
  useEffect(() => {
    const next = new Map<number, Cfg>()
    for (const rid of value.sharedRoleIds ?? []) {
      next.set(rid, { roleMode: 'all', pickedUserIds: new Set() })
    }
    const members = directoryQ.data?.members ?? []
    if (members.length === 0 && (value.sharedUserIds ?? []).length > 0) {
      // Defer per-role distribution until the directory resolves.
      setRoleSettings(next)
      return
    }
    const roleByUser = new Map<number, number>()
    for (const m of members) {
      if (m.role_id != null) roleByUser.set(m.user_id, m.role_id)
    }
    for (const uid of value.sharedUserIds ?? []) {
      const rid = roleByUser.get(uid)
      if (rid == null) continue
      const slot = next.get(rid)
      if (slot && slot.roleMode === 'all') continue
      const cfg = next.get(rid) ?? { roleMode: 'specific', pickedUserIds: new Set<number>() }
      cfg.roleMode = 'specific'
      cfg.pickedUserIds.add(uid)
      next.set(rid, cfg)
    }
    setRoleSettings(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value.sharedRoleIds?.join(','),
    value.sharedUserIds?.join(','),
    directoryQ.data,
  ])

  const allRoles = useMemo(() => rolesQ.data?.roles ?? [], [rolesQ.data])
  const filteredRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase()
    if (!q) return allRoles
    return allRoles.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q),
    )
  }, [allRoles, roleSearch])

  const membersByRole = useMemo(() => {
    const out = new Map<number, Array<{ user_id: number; name?: string | null; email: string }>>()
    for (const m of directoryQ.data?.members ?? []) {
      if (m.user_id === currentUserId) continue
      if (m.role_id == null) continue
      const list = out.get(m.role_id) ?? []
      list.push({ user_id: m.user_id, name: m.name, email: m.email })
      out.set(m.role_id, list)
    }
    return out
  }, [directoryQ.data, currentUserId])

  const emit = (settings: Map<number, Cfg>) => {
    const sharedRoleIds: number[] = []
    const sharedUserIds: number[] = []
    for (const [roleId, s] of settings) {
      if (s.roleMode === 'all') sharedRoleIds.push(roleId)
      else for (const u of s.pickedUserIds) sharedUserIds.push(u)
    }
    onChange({ sharedRoleIds, sharedUserIds })
  }

  const toggleRole = (id: number) => {
    setRoleSettings((prev) => {
      const next = new Map(prev)
      if (next.has(id)) next.delete(id)
      else next.set(id, { roleMode: 'all', pickedUserIds: new Set() })
      emit(next)
      return next
    })
    setOpenRoles((prev) => {
      const next = new Set(prev)
      if (!roleSettings.has(id)) next.add(id)
      else next.delete(id)
      return next
    })
  }
  const setRoleMode = (id: number, m: RoleMode) =>
    setRoleSettings((prev) => {
      const next = new Map(prev)
      const cur = next.get(id)
      if (!cur) return prev
      next.set(id, {
        roleMode: m,
        pickedUserIds: m === 'specific' ? cur.pickedUserIds : new Set(),
      })
      emit(next)
      return next
    })
  const toggleMember = (roleId: number, uid: number) =>
    setRoleSettings((prev) => {
      const next = new Map(prev)
      const cur = next.get(roleId)
      if (!cur) return prev
      const ids = new Set(cur.pickedUserIds)
      if (ids.has(uid)) ids.delete(uid)
      else ids.add(uid)
      next.set(roleId, { ...cur, pickedUserIds: ids })
      emit(next)
      return next
    })
  const toggleOpen = (id: number) =>
    setOpenRoles((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const totalRolesPicked = roleSettings.size
  const totalMembersPicked = useMemo(() => {
    let n = 0
    for (const s of roleSettings.values()) {
      if (s.roleMode === 'specific') n += s.pickedUserIds.size
    }
    return n
  }, [roleSettings])

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      {showHeader ? (
        <p className="text-xs text-muted-foreground">
          Pick one or more roles, then choose either every member of that
          role or just specific members from it.
        </p>
      ) : null}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={roleSearch}
          onChange={(e) => setRoleSearch(e.currentTarget.value)}
          placeholder="Search roles…"
          className="h-8 pl-7 text-xs"
        />
      </div>
      <div className="max-h-72 overflow-auto rounded-md border bg-card">
        {rolesQ.isLoading ? (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
            Loading roles…
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
            {allRoles.length === 0
              ? 'No roles in this workspace.'
              : 'No roles match your search.'}
          </div>
        ) : (
          filteredRoles.map((r) => {
            const cfg = roleSettings.get(r.id)
            const checked = !!cfg
            const expanded = openRoles.has(r.id)
            const roleMembers = membersByRole.get(r.id) ?? []
            const memberQ = (memberSearch[r.id] ?? '').trim().toLowerCase()
            const visibleMembers = memberQ
              ? roleMembers.filter(
                  (m) =>
                    (m.name ?? '').toLowerCase().includes(memberQ) ||
                    m.email.toLowerCase().includes(memberQ),
                )
              : roleMembers
            return (
              <div key={r.id} className="border-b last:border-b-0">
                <div className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleRole(r.id)}
                    aria-label={`Share with ${r.name}`}
                  />
                  <button
                    type="button"
                    onClick={() => (checked ? toggleOpen(r.id) : toggleRole(r.id))}
                    className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                  >
                    {checked ? (
                      expanded ? (
                        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      ) : (
                        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      )
                    ) : (
                      <span className="inline-block size-3.5 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium">{r.name}</span>
                        {r.is_system ? (
                          <Badge variant="secondary" className="text-[9px]">
                            system
                          </Badge>
                        ) : null}
                        {checked && cfg?.roleMode === 'specific' ? (
                          <Badge variant="info" className="text-[9px]">
                            {cfg.pickedUserIds.size} member
                            {cfg.pickedUserIds.size === 1 ? '' : 's'}
                          </Badge>
                        ) : null}
                      </div>
                      {r.description ? (
                        <div className="truncate text-[11px] text-muted-foreground">
                          {r.description}
                        </div>
                      ) : null}
                    </div>
                  </button>
                </div>
                {checked && expanded ? (
                  <div className="space-y-2 border-t bg-muted/20 px-2 py-2 text-xs">
                    <div className="flex items-center gap-3">
                      <label className="flex cursor-pointer items-center gap-1.5">
                        <input
                          type="radio"
                          className="size-3"
                          checked={cfg?.roleMode === 'all'}
                          onChange={() => setRoleMode(r.id, 'all')}
                        />
                        All members
                      </label>
                      <label className="flex cursor-pointer items-center gap-1.5">
                        <input
                          type="radio"
                          className="size-3"
                          checked={cfg?.roleMode === 'specific'}
                          onChange={() => setRoleMode(r.id, 'specific')}
                          disabled={roleMembers.length === 0}
                        />
                        Specific members
                      </label>
                    </div>
                    {cfg?.roleMode === 'specific' ? (
                      <>
                        <Input
                          value={memberSearch[r.id] ?? ''}
                          onChange={(e) =>
                            setMemberSearch((prev) => ({
                              ...prev,
                              [r.id]: e.currentTarget.value,
                            }))
                          }
                          placeholder="Search members…"
                          className="h-7 text-xs"
                        />
                        <div className="max-h-40 overflow-auto rounded border bg-card">
                          {directoryQ.isLoading ? (
                            <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">
                              Loading members…
                            </div>
                          ) : visibleMembers.length === 0 ? (
                            <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">
                              {roleMembers.length === 0
                                ? 'No active members in this role.'
                                : 'No members match your search.'}
                            </div>
                          ) : (
                            visibleMembers.map((m) => {
                              const checkedMember = cfg.pickedUserIds.has(m.user_id)
                              return (
                                <label
                                  key={m.user_id}
                                  className="flex cursor-pointer items-center gap-2 border-b px-2 py-1 last:border-b-0 hover:bg-accent"
                                >
                                  <Checkbox
                                    checked={checkedMember}
                                    onCheckedChange={() =>
                                      toggleMember(r.id, m.user_id)
                                    }
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate font-medium">
                                      {m.name || m.email}
                                    </div>
                                    {m.name ? (
                                      <div className="truncate text-[10px] text-muted-foreground">
                                        {m.email}
                                      </div>
                                    ) : null}
                                  </div>
                                </label>
                              )
                            })
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )
          })
        )}
      </div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          {totalRolesPicked} role{totalRolesPicked === 1 ? '' : 's'} ·{' '}
          {totalMembersPicked} member{totalMembersPicked === 1 ? '' : 's'} picked.
        </span>
        {totalRolesPicked > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-[11px]"
            onClick={() => {
              setRoleSettings(new Map())
              emit(new Map())
            }}
          >
            Clear all
          </Button>
        ) : null}
      </div>
    </div>
  )
}
